// Cognito, without an SDK (design §4): the hosted UI's authorization-code flow + PKCE, sent straight to a company's
// identity provider when discovery found one (identity_provider=co-…), else to the email + password page; and the
// public SignUp / ConfirmSignUp calls for self-serve sign-up. Tokens live in sessionStorage (gone when the tab
// closes); the 12-hour refresh token renews them quietly.

export interface AppConfig {
  region: string;
  userPoolId: string;
  clientId: string;
  loginDomain: string;
  stage: string;
  /** Cloudflare Turnstile site key; empty = no bot check (mock / until set up). */
  turnstileSiteKey?: string;
  landingUrl?: string;
  /** Mock server only: sign in without Cognito. */
  mock?: boolean;
}
interface Tokens {
  idToken: string;
  refreshToken?: string;
  expiresAt: number;
}

let cfg: AppConfig | null = null;
const TOKENS = "esignit-auth";
const PENDING = "esignit-auth-pending";
const redirectUri = (path = "/") => `${location.origin}${path}`;

const safe = <T,>(fn: () => T, fallback: T): T => {
  try {
    return fn();
  } catch {
    return fallback;
  }
};

export async function loadConfig(): Promise<AppConfig> {
  if (cfg) return cfg;
  const r = await fetch("/config.json", { cache: "no-store" });
  if (!r.ok) throw new Error("config.json is missing");
  cfg = (await r.json()) as AppConfig;
  return cfg;
}
export const config = () => cfg!;

const read = (): Tokens | null => safe(() => JSON.parse(sessionStorage.getItem(TOKENS) ?? "null"), null);
const write = (t: Tokens | null) => safe(() => (t ? sessionStorage.setItem(TOKENS, JSON.stringify(t)) : sessionStorage.removeItem(TOKENS)), undefined);
/** The mock server hands out tokens itself. */
export const setMockToken = (idToken: string) => write({ idToken, expiresAt: Date.now() + 3600_000 });

const b64url = (b: Uint8Array) => btoa(String.fromCharCode(...b)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
const random = (n = 32) => b64url(crypto.getRandomValues(new Uint8Array(n)));
const challenge = async (v: string) => b64url(new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(v))));

async function token(body: Record<string, string>) {
  const res = await fetch(`https://${cfg!.loginDomain}/oauth2/token`, {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({ client_id: cfg!.clientId, ...body }),
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.error_description ?? d.error ?? "Sign-in failed");
  return d;
}
const toTokens = (d: any, keep?: string): Tokens => ({ idToken: d.id_token, refreshToken: d.refresh_token ?? keep, expiresAt: Date.now() + Number(d.expires_in ?? 3600) * 1000 });

async function authorizeUrl(o: { provider: string; loginHint?: string; back: string; path: string; key: string }) {
  const verifier = random(48);
  const state = random(12);
  safe(() => sessionStorage.setItem(o.key, JSON.stringify({ verifier, state, back: o.back })), undefined);
  const q = new URLSearchParams({
    client_id: cfg!.clientId,
    response_type: "code",
    scope: "openid email profile",
    redirect_uri: redirectUri(o.path),
    state,
    code_challenge: await challenge(verifier),
    code_challenge_method: "S256",
    identity_provider: o.provider,
    ...(o.loginHint ? { login_hint: o.loginHint } : {}),
  });
  return `https://${cfg!.loginDomain}/oauth2/authorize?${q}`;
}

/** Sign in: through a company IdP (co-…) or the email + password page (COGNITO). */
export async function signIn(o: { provider?: string; loginHint?: string; back?: string } = {}) {
  if (cfg?.mock) return location.assign(`/mock-signin?${new URLSearchParams({ back: o.back ?? location.pathname + location.search, ...(o.loginHint ? { as: o.loginHint } : {}), ...(o.provider ? { provider: o.provider } : {}) })}`);
  location.assign(await authorizeUrl({ provider: o.provider ?? "COGNITO", loginHint: o.loginHint, back: o.back ?? location.pathname + location.search, path: "/", key: PENDING }));
}

async function finish(key: string, path: string): Promise<{ error: string | null; tokens?: Tokens }> {
  const p = new URLSearchParams(location.search);
  // Only returns to our own redirect path (connector callbacks also carry ?code=…&state=…).
  if (location.pathname !== path || (!p.has("code") && !p.has("error"))) return { error: null };
  const pending = safe(() => JSON.parse(sessionStorage.getItem(key) ?? "null"), null) as { verifier: string; state: string; back: string } | null;
  safe(() => sessionStorage.removeItem(key), undefined);
  history.replaceState(null, "", pending?.back ?? "/");
  if (p.has("error")) {
    const desc = p.get("error_description") ?? "";
    // Trigger rejections come back as "PreSignUp failed with error <message>." (or PreAuthentication).
    return { error: desc.replace(/^Pre\w+ failed with error\s*/i, "").replace(/\.$/, "") || "Sign-in was cancelled" };
  }
  if (!pending || pending.state !== p.get("state")) return { error: "That sign-in link expired. Please sign in again." };
  try {
    return { error: null, tokens: toTokens(await token({ grant_type: "authorization_code", code: p.get("code")!, redirect_uri: redirectUri(path), code_verifier: pending.verifier })) };
  } catch (e) {
    return { error: (e as Error).message };
  }
}

/** Call on load: finishes a sign-in return (?code=…&state=…). Returns an error message or null. */
export async function completeSignIn(): Promise<string | null> {
  const r = await finish(PENDING, "/");
  if (r.tokens) write(r.tokens);
  return r.error;
}

// ---------- single sign-on test (admin console pop-up) ----------

const SSO_TEST = "esignit-sso-test";
/** Opens a pop-up that signs in through the company IdP and reports back; the admin's own session is untouched. */
export async function startSsoTest(provider: string, email?: string) {
  if (cfg?.mock) return window.open(`/mock-sso-test?${new URLSearchParams({ provider, as: email ?? "" })}`, "esignit-sso-test", "popup,width=520,height=720");
  const url = await authorizeUrl({ provider, back: "/sso-test", path: "/sso-test", key: SSO_TEST });
  return window.open(url, "esignit-sso-test", "popup,width=520,height=720");
}
/** In the pop-up: exchanges the code; the ID token is used once and never stored. */
export async function finishSsoTest(): Promise<{ error: string | null; idToken?: string }> {
  const r = await finish(SSO_TEST, "/sso-test");
  return { error: r.error, idToken: r.tokens?.idToken };
}

let refreshing: Promise<Tokens | null> | null = null;
export async function idToken(): Promise<string | null> {
  let t = read();
  if (!t) return null;
  if (t.expiresAt - Date.now() < 60_000) {
    if (cfg?.mock) return t.idToken;
    const cur = t;
    refreshing ??= (cur.refreshToken ? token({ grant_type: "refresh_token", refresh_token: cur.refreshToken }).then((d) => toTokens(d, cur.refreshToken)) : Promise.resolve(null))
      .catch(() => null)
      .then((n) => {
        write(n);
        return n;
      })
      .finally(() => (refreshing = null));
    t = await refreshing;
  }
  return t?.idToken ?? null;
}

/** Forget the session here and show the sign-in page (expired or rejected token). */
export function signOutLocal() {
  write(null);
  const back = location.pathname + location.search;
  location.assign(back === "/" ? "/" : `/?${new URLSearchParams({ back })}`);
}

export async function signOut() {
  const t = read();
  write(null);
  if (cfg?.mock) return location.assign("/");
  if (t?.refreshToken)
    await fetch(`https://${cfg!.loginDomain}/oauth2/revoke`, { method: "POST", headers: { "content-type": "application/x-www-form-urlencoded" }, body: new URLSearchParams({ token: t.refreshToken, client_id: cfg!.clientId }) }).catch(() => undefined);
  location.assign(`https://${cfg!.loginDomain}/logout?${new URLSearchParams({ client_id: cfg!.clientId, logout_uri: redirectUri() })}`);
}

// ---------- sign-up (public Cognito API; no secret, no SDK) ----------

async function cognito<T = any>(action: string, body: Record<string, unknown>): Promise<T> {
  if (cfg?.mock) {
    const r = await fetch(`/mock/cognito/${action}`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.message ?? "Sign-up failed");
    return d;
  }
  const res = await fetch(`https://cognito-idp.${cfg!.region}.amazonaws.com/`, {
    method: "POST",
    headers: { "content-type": "application/x-amz-json-1.1", "x-amz-target": `AWSCognitoIdentityProviderService.${action}` },
    body: JSON.stringify({ ClientId: cfg!.clientId, ...body }),
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = String(d.message ?? d.__type ?? "Sign-up failed").replace(/^PreSignUp failed with error\s*/i, "").replace(/\.$/, "");
    throw new Error(/UsernameExists/i.test(String(d.__type)) ? "That email already has an account. Sign in instead." : msg);
  }
  return d as T;
}

export const signUp = (o: { email: string; password: string; name: string; ticket?: string }) =>
  cognito("SignUp", { Username: o.email, Password: o.password, UserAttributes: [{ Name: "email", Value: o.email }, { Name: "name", Value: o.name }], ...(o.ticket ? { ClientMetadata: { ticket: o.ticket } } : {}) });
export const confirmSignUp = (email: string, code: string) => cognito("ConfirmSignUp", { Username: email, ConfirmationCode: code });
export const resendCode = (email: string) => cognito("ResendConfirmationCode", { Username: email });
